import { registerNewBatch, deliverFiles } from "../../../../upload/services/batchActions";
import { stageFilesLocally, finalizeLocalBatch } from "../../../../upload/services/storageService";
import { PromotionPayload } from "../../../../upload/dtos/uploadModels";

/**
 * 🚀 SAVE ESG TAXONOMY CONTROLLER
 * Isolated logic for promoting validated "Single Pot" JSON to the Clean S3 Route.
 * Handlers are mapped to use the aligned PromotionPayload DTO.
 */
export const SaveESG_taxonomyController = {
    /**
     * promoteToCleanRoute
     * @param {Array} fullPot - The integrated taxonomy JSON
     * @param {String} siteKey - Current site identifier
     * @param {Object} diagnosticPayload - Optional pre-built FLATTENED DTO for testing
     */
    promoteToCleanRoute: async (fullPot, siteKey, diagnosticPayload = null) => {
        console.group("🚀 [TAXONOMY_PROMOTION] Initiating Clean Route Delivery");
        
        try {
            let finalPayload;
            let fileName;

            // --- 1. PAYLOAD RESOLUTION ---
            if (diagnosticPayload) {
                // Diagnostic Mode: Payload is already flattened via .toJSON() in Workflow_Hub
                // Ensure we have a clean object
                finalPayload = diagnosticPayload.toJSON ? diagnosticPayload.toJSON() : diagnosticPayload;
                fileName = finalPayload.fileNames[0];
                console.log("🧪 [DIAGNOSTIC_MODE] Using provided flattened test payload.");
            } else {
                // REAL SESSION: User Prompts for Discovery Labels
                const taxonomyName = window.prompt("Enter a unique name for this Taxonomy version:", "ESRS_Standard_v1");
                if (!taxonomyName) {
                    console.warn("Promotion cancelled by user.");
                    console.groupEnd();
                    return { success: false, reason: "CANCELLED" };
                }

                const validFrom = window.prompt("Enter the 'Valid From' date (YYYY-MM-DD):", new Date().toISOString().split('T')[0]);
                
                fileName = `${taxonomyName.replace(/\s+/g, '_')}_${new Date().getTime()}.json`;

                // Build and Flatten the DTO manually
                // The constructor uses 'targetBucket' and 'targetRoot' internally for alignment
                finalPayload = new PromotionPayload(siteKey, Date.now(), fileName)
                    .addMetadata("taxonomy-name", taxonomyName)
                    .addMetadata("valid-from", validFrom)
                    .addMetadata("status", "Draft")
                    .addMetadata("version-label", "Validated_Sync")
                    .toJSON(); 
            }

            // --- 2. VIRTUAL FILE CREATION ---
            const blob = new Blob([JSON.stringify(fullPot, null, 2)], { type: "application/json" });
            const virtualFile = new File([blob], fileName, { type: "application/json" });

            // --- 3. BFF HANDSHAKE ---
            // registerNewBatch now receives the flat payload with 'targetRoot'
            console.log(`📡 [STEP 1] Registering Batch Authority: ${fileName}`);
            const newBatch = await registerNewBatch([virtualFile], { siteKey }, finalPayload);

            // --- 4. LOCAL STAGING (Dexie) ---
            console.log(`💾 [STEP 2] Staging Virtual Bits (Batch: ${newBatch.batchId})`);
            await stageFilesLocally([virtualFile], newBatch.batchId, siteKey);
            
            // finalizeLocalBatch persists the flattened payload so QueueManager has it for the signature request
            // This ensures S3CorsValue: "esg" is correctly saved to the Dexie record.
            await finalizeLocalBatch(
                newBatch.batchId, 
                siteKey, 
                newBatch.hash, 
                newBatch.timestamp, 
                finalPayload
            );

            // --- 5. S3 DELIVERY ---
            console.log(`🛰️ [STEP 3] Handing off to QueueManager for S3 Delivery`);
            // DeliverFiles triggers ApplyForSignedURL, which pulls the Clean Bucket target from Dexie
            await deliverFiles(newBatch.batchId, [virtualFile], siteKey);

            console.groupEnd();
            return { 
                success: true, 
                batchId: newBatch.batchId, 
                fileName,
                fullPayload: finalPayload 
            };

        } catch (err) {
            console.error("💀 [PROMOTION_FAILED]", err);
            console.groupEnd();
            throw err;
        }
    }
};